import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface OfferService {
  id: string;
  offer_id: string;
  service_id: string;
  level: string;
  display_order: number;
  is_active: boolean;
  description?: string;
  price?: number;
  price_unit?: string; // Ajout direct du price_unit
  images?: string[];
  level_title?: string;
  level_subtitle?: string;
  level_emoji?: string;
  level_color?: string;
  level_badge_color?: string;
  is_recommended?: boolean;
  created_at: string;
  updated_at: string;
  service?: {
    id: string;
    title: string;
    description?: string;
    base_price: number;
    price_unit: string;
    quantifiable?: boolean;
    image_url?: string;
    show_price: boolean;
    service_category?: {
      id: string;
      name: string;
      slug: string;
      color: string;
    };
  };
}

// Fonction utilitaire pour obtenir les informations de niveau réelles
export const getRealLevelInfo = (level: string, offerServices?: any[]) => {
  // Chercher les informations personnalisées dans les données de l'offre
  const customLevelData = offerServices?.find(os => os.level === level);
  
  switch (level.toLowerCase()) {
    case 'essentiel':
      return {
        title: customLevelData?.level_title || 'Essentiel',
        subtitle: customLevelData?.level_subtitle || 'L\'essentiel pour votre évènement',
        emoji: customLevelData?.level_emoji || '🥉',
        color: customLevelData?.level_color || 'border-l-green-500 bg-green-50/30',
        badgeColor: customLevelData?.level_badge_color || 'bg-green-100 text-green-800'
      };
    case 'confort':
      return {
        title: customLevelData?.level_title || 'Confort',
        subtitle: customLevelData?.level_subtitle || 'Plus de prestations pour votre confort',
        emoji: customLevelData?.level_emoji || '🥈',
        color: customLevelData?.level_color || 'border-l-blue-500 bg-blue-50/30',
        badgeColor: customLevelData?.level_badge_color || 'bg-blue-100 text-blue-800'
      };
    case 'signature':
      return {
        title: customLevelData?.level_title || 'Signature',
        subtitle: customLevelData?.level_subtitle || 'L\'excellence pour un évènement d\'exception',
        emoji: customLevelData?.level_emoji || '🥇',
        color: customLevelData?.level_color || 'border-l-purple-500 bg-purple-50/30',
        badgeColor: customLevelData?.level_badge_color || 'bg-purple-100 text-purple-800'
      };
    case 'autre':
    case 'hors_categorie':
      return {
        title: customLevelData?.level_title || 'Hors catégorie',
        subtitle: customLevelData?.level_subtitle || 'Prestations spécialisées et sur-mesure',
        emoji: customLevelData?.level_emoji || '✨',
        color: customLevelData?.level_color || 'border-l-amber-500 bg-amber-50/30',
        badgeColor: customLevelData?.level_badge_color || 'bg-amber-100 text-amber-800'
      };
    default:
      return {
        title: 'Standard',
        subtitle: 'Prestation standard',
        emoji: '⭐',
        color: 'border-l-gray-500 bg-gray-50/30',
        badgeColor: 'bg-gray-100 text-gray-800'
      };
  }
};

// Fonction utilitaire pour obtenir l'affichage du prix réel
export const getRealPriceDisplay = (offerService: any) => {
  const service = offerService.service;
  let price = offerService.price || service?.base_price || 0;
  
  // CORRECTION: Utiliser d'abord le price_unit direct d'offer_service, puis fallback sur service
  const priceUnit = offerService.price_unit || service?.price_unit || 'par_prestation';
  let unitText = '';
  
  switch (priceUnit) {
    case 'par_personne':
      unitText = '/ personne';
      break;
    case 'par_heure':
      unitText = '/ heure';
      break;
    case 'par_jour':
      unitText = '/ jour';
      break;
    case 'par_prestation':
    default:
      unitText = 'forfait';
      break;
  }
  
  return { price, unitText, priceUnit };
};

export const useOfferServices = (offerId: string) => {
  return useQuery({
    queryKey: ['offer-services', offerId],
    queryFn: async () => {
      console.log('🔄 Chargement des services pour l\'offre:', offerId);
      
      const { data, error } = await supabase
        .from('offer_services')
        .select(`
          *,
          price_unit,
          service:services(
            id,
            title,
            description,
            base_price,
            price_unit,
            quantifiable,
            image_url,
            show_price,
            service_category:service_categories(
              id,
              name,
              slug,
              color
            )
          )
        `)
        .eq('offer_id', offerId)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('❌ Erreur lors du chargement des services de l\'offre:', error);
        throw error;
      }

      console.log('✅ Services de l\'offre chargés avec catégories:', data?.map(s => ({ 
        title: s.service?.title, 
        category: s.service?.service_category?.name 
      })) || []);
      return data as OfferService[] || [];
    },
    enabled: !!offerId,
    staleTime: 0, // Force refresh to clear cache
    refetchOnWindowFocus: true
  });
};
